import Quill from 'quill';

const BlockEmbed = Quill.import('blots/block/embed');

class ButtonBlot extends BlockEmbed {
  static update(domNode, value) {
    // Find the button element
    const button = domNode.querySelector('a');
    if (!button) return;
    
    // Update button properties
    button.href = value.link || '#';
    button.target = value.link ? '_blank' : '_self';
    button.textContent = value.text || 'Click Here';
    
    // Update CSS classes
    button.className = value.cssClass || '';
    if (value.cssClass) {
      button.setAttribute('data-css-class', value.cssClass);
    } else {
      button.removeAttribute('data-css-class');
    }
    
    // Update container alignment
    domNode.style.textAlign = value.alignment || 'center';
    
    // Update button styles
    const styles = {
      display: 'inline-block',
      padding: value.size === 'small' ? '6px 16px' : value.size === 'large' ? '12px 32px' : '8px 24px',
      backgroundColor: value.style === 'outlined' ? 'transparent' : value.backgroundColor,
      color: value.style === 'outlined' ? value.backgroundColor : value.textColor,
      border: value.style === 'outlined' ? `2px solid ${value.backgroundColor}` : 'none',
      borderRadius: `${value.borderRadius}px`,
      textDecoration: 'none',
      fontWeight: '600',
      fontSize: value.size === 'small' ? '14px' : value.size === 'large' ? '18px' : '16px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: 'inherit',
    };
    
    // Apply styles
    Object.assign(button.style, styles);
    
    // Update stored data
    button.setAttribute('data-button-config', JSON.stringify(value));
    
    // Update hover handlers
    const hoverStyles = value.style === 'outlined' 
      ? {
          backgroundColor: value.backgroundColor,
          color: value.textColor,
        }
      : {
          opacity: '0.9',
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
        };
    
    // Remove old listeners
    const newButton = button.cloneNode(true);
    button.parentNode.replaceChild(newButton, button);
    
    // Add new listeners
    newButton.addEventListener('mouseenter', function() {
      Object.assign(this.style, hoverStyles);
    });
    
    newButton.addEventListener('mouseleave', function() {
      Object.assign(this.style, styles);
    });
    
    newButton.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
    });
  }
  
  static create(value) {
    const node = super.create();
    
    // Apply container styles directly to node
    node.style.textAlign = value.alignment || 'center';
    node.style.margin = '16px 0';
    node.style.clear = 'both';
    
    // Create the button/link element
    const button = document.createElement('a');
    button.href = value.link || '#';
    button.target = value.link ? '_blank' : '_self';
    button.rel = 'noopener noreferrer';
    button.textContent = value.text || 'Click Here';
    
    // Apply CSS classes if provided
    if (value.cssClass) {
      button.className = value.cssClass;
      // Add data attribute for CSS targeting
      button.setAttribute('data-css-class', value.cssClass);
    }
    
    // Apply all the styles
    const styles = {
      display: 'inline-block',
      padding: value.size === 'small' ? '6px 16px' : value.size === 'large' ? '12px 32px' : '8px 24px',
      backgroundColor: value.style === 'outlined' ? 'transparent' : value.backgroundColor,
      color: value.style === 'outlined' ? value.backgroundColor : value.textColor,
      border: value.style === 'outlined' ? `2px solid ${value.backgroundColor}` : 'none',
      borderRadius: `${value.borderRadius}px`,
      textDecoration: 'none',
      fontWeight: '600',
      fontSize: value.size === 'small' ? '14px' : value.size === 'large' ? '18px' : '16px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: 'inherit',
    };
    
    // Apply styles to button
    Object.assign(button.style, styles);
    
    // Store data for retrieval
    button.setAttribute('data-button-config', JSON.stringify(value));
    
    // Add hover effects using mouse events
    const hoverStyles = value.style === 'outlined' 
      ? {
          backgroundColor: value.backgroundColor,
          color: value.textColor,
        }
      : {
          opacity: '0.9',
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
        };
    
    button.addEventListener('mouseenter', function() {
      Object.assign(this.style, hoverStyles);
    });
    
    button.addEventListener('mouseleave', function() {
      Object.assign(this.style, styles);
    });
    
    // Prevent navigation in editor mode
    button.addEventListener('click', function(e) {
      // Always prevent click in editor
      e.preventDefault();
      e.stopPropagation();
    });
    
    // Add double-click to edit
    node.addEventListener('dblclick', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      // Get the latest value from the button
      const button = this.querySelector('a[data-button-config]');
      let currentValue = value;
      if (button) {
        try {
          currentValue = JSON.parse(button.getAttribute('data-button-config'));
        } catch (err) {
          // Use original value if parse fails
        }
      }
      
      // Trigger edit event
      const editEvent = new CustomEvent('button-edit', {
        detail: { node: this, value: currentValue },
        bubbles: true
      });
      this.dispatchEvent(editEvent);
    });
    
    node.appendChild(button);
    
    return node;
  }
  
  static value(node) {
    const button = node.querySelector('a[data-button-config]');
    if (button) {
      try {
        return JSON.parse(button.getAttribute('data-button-config'));
      } catch (e) {
        return {};
      }
    }
    return {};
  }
}

ButtonBlot.blotName = 'button';
ButtonBlot.tagName = 'div';
ButtonBlot.className = 'ql-custom-button';

export default ButtonBlot;